<script setup>
import Spinner from '@/components/shared/core/Spinner.vue'
import Icon from '@/components/shared/icons/Icon.vue'
import {useEmptySlotsCheck} from '@/composables/use-empty-slots-check.js'
import {useRouting} from '@/composables/use-routing.js'
import {toRefs, computed} from 'vue'

//Emits and props
const emit = defineEmits([
  'click',
])
const props = defineProps({
  type: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  iconSize: {
    type: String,
    default: 'm',
  },
  iconRotate: {
    type: Number,
    default: 0,
    validator: (value) => value >= -360 && value <= 360,
  },
  isIconOutlined: {
    type: Boolean,
    default: false,
  },
  route: {
    type: [String, Object],
    default: null,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isBlock: {
    type: Boolean,
    default: false,
  },
  isSlim: {
    type: Boolean,
    default: false,
  },
  isCompact: {
    type: Boolean,
    default: false,
  },
  isDanger: {
    type: Boolean,
    default: false,
  },
  hasSpinner: {
    type: Boolean,
    default: false,
  },
  testId: {
    type: String,
    default: '',
  },
})

//Extract props and get router
const {isDisabled, route, type} = toRefs(props)
const {router} = useRouting()

//Handle click
const handleClick = (event) => {

  //Disabled
  if (isDisabled.value) {
    return
  }

  //Route provided
  if (route.value) {
    return router.push(route.value)
  }

  //Emit click event
  emit('click', event)
}

//Spinner color
const spinnerColor = computed(() => {
  if (type.value === 'primary') {
    return 'white'
  }
  return ''
})

//Check if default slot is empty
const {isSlotEmpty} = useEmptySlotsCheck()
</script>

<template>
  <button
    :test-id="testId"
    :disabled="isDisabled || hasSpinner"
    :class="{
      [$style.button]: true,
      [$style.block]: isBlock,
      [$style.slim]: isSlim,
      [$style.compact]: isCompact,
      [$style.danger]: isDanger,
      [$style[type]]: !!type,
      [$style.withSpinner]: hasSpinner,
      [$style.iconOnly]: (isSlotEmpty && !label),
    }"
    tabindex="0"
    type="button"
    v-bind="$attrs"
    @click="handleClick($event)"
  >
    <slot name="icon">
      <Icon
        v-if="icon"
        :class="$style.icon"
        :icon="icon"
        :size="iconSize"
        :rotate="iconRotate"
        :is-outlined="isIconOutlined"
        role="presentation"
        aria-hidden="true"
      />
    </slot>
    <span
      :class="$style.label"
      hide-empty
    >
      <slot name="label">{{ label }}</slot>
    </span>
    <slot />
    <Spinner
      v-if="hasSpinner"
      :class="$style.spinner"
      :color="spinnerColor"
    />
  </button>
</template>

<style module lang="scss">
.button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: var(--button-height);
  padding: 0.5rem 0.75rem;
  border: 2px solid transparent;
  border-radius: var(--border-radius-m);
  vertical-align: middle;
  font-family: var(--font-family);
  line-height: var(--font-size-buttons-normal);
  font-size: var(--font-size-buttons-normal);
  font-weight: var(--font-weight-normal);
  background: transparent;
  color: var(--color-text);
  outline: none;
  transform: none !important;
  transition: background-color var(--transition-cubic-fast), border-color var(--transition-cubic-fast), color var(--transition-cubic-fast);
  overflow: hidden;
  @media (min-width: 768px) {
    padding: 0.5rem 1.5rem;
  }

  //Block buttons
  &.block {
    display: flex;
    width: 100%;
    @media (min-width: 768px) {
      width: auto;
    }
  }

  //Slim buttons
  &.slim {
    padding: 0.5rem 1rem;
  }

  //Icon only button
  &.iconOnly {
    padding: 0;
    height: var(--button-height);
    width: var(--button-height);
    min-width: var(--button-height);
    border-radius: 50%;
    &.compact {
      height: var(--button-height-compact);
      width: var(--button-height-compact);
      min-width: var(--button-height-compact);
    }
  }

  //General modifier states
  &:hover:not(:disabled) {
    cursor: pointer;
  }
  &:active, &:focus {
    outline: none;
  }
  &:active:not(:disabled) {
    transform: translateX(1px) translateY(1px);
  }
  &:disabled {
    cursor: not-allowed;
    &.withSpinner {
      color: transparent;
    }
    .spinner {
      display: flex;
    }
  }

  //Primary and secondary button types
  &.primary {
    background-color: var(--bg-primary-element);
    color: var(--color-white);
    text-shadow: 0px 0px 1px var(--color-white); /* Bold text will shift the width of the button */
    &:hover:not(:disabled) {
      background-color: var(--bg-primary-element-hover);
      border-color: var(--bg-primary-element-hover);
    }
    &:focus-visible:not(:disabled) {
      background-color: var(--bg-primary-element-hover);
      border-color: var(--p-form-field-focus-border-color);
    }
    &:disabled {
      opacity: 0.5;
    }
  }
  &.secondary {
    color: var(--color-text);
    background: var(--bg-interactive-element);
    &:hover:not(:disabled) {
      background-color: var(--bg-interactive-element-hover);
      &.danger {
        color: var(--color-text-danger);
        border-color: var(--color-status-danger);
      }
    }
    &:focus-visible:not(:disabled) {
      border-color: var(--p-form-field-focus-border-color);
      color: var(--color-text);
    }
    &:disabled {
      color: var(--color-text-muted);
    }
  }

  //Text and link button types
  &.text, &.link {
    padding: 0;
    &:hover:not(:disabled), &:focus-visible:not(:disabled) {
      .label {
        text-decoration: underline;
      }
    }
    &:active:not(:disabled) {
      transform: none;
    }
    &:disabled {
      opacity: 0.5;
    }
  }
  &.text {
    color: var(--color-text-muted);
    &:hover:not(:disabled) {
      color: var(--color-text);
      &.danger {
        color: var(--color-text-danger);
      }
    }
    &:focus-visible:not(:disabled) {
      border-color: var(--p-form-field-focus-border-color);
    }
  }
  &.link {
    color: var(--color-text-link);
  }
}
.spinner {
  display: none;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background-color: inherit;
  border-radius: var(--border-radius-m);
}
.label {
  white-space: nowrap;
}
.icon, .label {
  transition: color var(--transition-cubic-fast);
}
</style>
